import { useState, type FormEvent } from "react";
import type { MeResponse } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import { FormField } from "@/shared/components/form/FormField";
import { PasswordFormField } from "@/shared/components/form/PasswordFormField";
import { useChangeEmail } from "@/shared/lib/auth";

/**
 * Change the account email. Requires the current password; the change is
 * immediate (no verification email) and signs out other devices.
 */
export function ChangeEmailForm({ user }: { user: MeResponse }) {
  const change = useChangeEmail();
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    change.mutate(
      { email: email.trim(), currentPassword },
      {
        onSuccess: () => {
          setEmail("");
          setCurrentPassword("");
        },
      },
    );
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <FormField
        label="New email"
        type="email"
        name="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          change.reset();
        }}
        hint={`Your email is currently ${user.email}.`}
      />
      <PasswordFormField
        label="Current password"
        name="currentPassword"
        autoComplete="current-password"
        required
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
      />
      {change.error ? (
        <p className="type-body-sm text-danger-strong mx-4">{change.error.message}</p>
      ) : change.isSuccess ? (
        <p className="type-body-sm text-text-muted mx-4">
          Email updated. Other devices have been signed out.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={change.isPending}>
          {change.isPending ? "Saving…" : "Update email"}
        </Button>
      </div>
    </form>
  );
}
